import { useState, useEffect } from 'react';
import {
  AlertTriangle, Clock, UserX, Fingerprint,
  Star, Trophy, ChevronDown, ChevronUp,
  Users, CalendarDays, TrendingDown, TrendingUp, Plane, Timer,
} from 'lucide-react';
import StatCard from '../Cards/StatCard';
import { attendanceAPI } from '../../services/api';

// ─── helpers ─────────────────────────────────────────────────────────────────

function attStatColor(pct) {
  if (pct == null) return 'blue';
  if (pct >= 95)   return 'green';
  if (pct >= 90)   return 'yellow';
  return 'red';
}

const PERIOD_OPTIONS = [
  { value: 'today',      label: 'Today'            },
  { value: 'month',      label: 'Current Month'    },
  { value: 'prev_month', label: 'Previous Month'   },
  { value: 'session',    label: 'Academic Session' },
  { value: 'custom',     label: 'Custom'           },
];

const ALERT_ICONS = {
  low_attendance:   { Icon: AlertTriangle, color: 'text-red-500'    },
  habitual_late:    { Icon: Clock,         color: 'text-amber-500'  },
  unplanned_absent: { Icon: UserX,         color: 'text-orange-500' },
  missing_punch:    { Icon: Fingerprint,   color: 'text-slate-500'  },
};

const ALERT_MSG = {
  low_attendance:   a => `${a.employee} — attendance ${a.value}%, below threshold`,
  habitual_late:    a => `${a.employee} — late ${a.value} times`,
  unplanned_absent: a => `${a.employee} — absent ${a.value} days`,
  missing_punch:    a => `${a.employee} — ${a.value} missing punch record(s)`,
};

// ─── skeletons ───────────────────────────────────────────────────────────────

function CardSkeleton() {
  return (
    <div className="glass-card p-4 space-y-2 animate-pulse">
      <div className="h-3 w-24 bg-gray-200 rounded" />
      <div className="h-7 w-16 bg-gray-200 rounded" />
      <div className="h-3 w-32 bg-gray-200 rounded" />
    </div>
  );
}

function SkeletonRow({ count }) {
  return (
    <div className={`grid gap-4 grid-cols-2 ${count === 6 ? 'sm:grid-cols-3 lg:grid-cols-6' : count === 5 ? 'sm:grid-cols-3 lg:grid-cols-5' : count === 4 ? 'lg:grid-cols-4' : 'grid-cols-2'}`}>
      {Array.from({ length: count }).map((_, i) => <CardSkeleton key={i} />)}
    </div>
  );
}

// ─── section header ───────────────────────────────────────────────────────────

function SectionTitle({ children }) {
  return (
    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
      {children}
    </h2>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function EnhancedDashboard({ yearId }) {
  const [period,       setPeriod]       = useState('month');
  const [customFrom,   setCustomFrom]   = useState('');
  const [customTo,     setCustomTo]     = useState('');
  const [pendingFrom,  setPendingFrom]  = useState('');
  const [pendingTo,    setPendingTo]    = useState('');
  const [riskThreshold, setRiskThreshold] = useState(90);
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [riskExpanded,   setRiskExpanded]   = useState(false);

  useEffect(() => {
    if (!yearId) return;
    async function fetchData() {
      setLoading(true);
      try {
        const params = { academic_year_id: yearId, period, risk_threshold: riskThreshold };
        if (period === 'custom' && customFrom && customTo) {
          params.from_date = customFrom;
          params.to_date   = customTo;
        }
        const res = await attendanceAPI.getEnhancedStats(params);
        setData(res.data);
      } catch (err) {
        if (err.name !== 'CanceledError') console.error('EnhancedDashboard fetch error', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [yearId, period, customFrom, customTo, riskThreshold]);

  const s    = data?.summary     || {};
  const ex   = data?.exceptions  || {};
  const pos  = data?.positives   || {};
  const comp = data?.compliance  || {};
  const dept = data?.departments || {};
  const alerts   = data?.alerts         || [];
  const riskList = data?.risk_employees || [];

  const visibleAlerts = alertsExpanded ? alerts : alerts.slice(0, 10);

  return (
    <div className="space-y-6">

      {/* ── Period filter bar ─────────────────────────────────────────────── */}
      <div className="glass-card p-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                period === opt.value
                  ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {period === 'custom' && (
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" value={pendingFrom} onChange={e => setPendingFrom(e.target.value)}
              className="border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
            <span className="text-slate-400 text-sm">to</span>
            <input type="date" value={pendingTo} onChange={e => setPendingTo(e.target.value)}
              className="border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
            <button
              onClick={() => { setCustomFrom(pendingFrom); setCustomTo(pendingTo); }}
              disabled={!pendingFrom || !pendingTo}
              className="px-3 py-1 bg-primary-600 text-white text-sm rounded-lg disabled:opacity-40 hover:bg-primary-700 transition-colors"
            >
              Apply
            </button>
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-slate-500 whitespace-nowrap">Risk Threshold</span>
          <input
            type="number" min={1} max={99} value={riskThreshold}
            onChange={e => setRiskThreshold(Number(e.target.value))}
            className="w-14 border border-slate-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <span className="text-sm text-slate-500">%</span>
        </div>
      </div>

      {/* ── Section 1: Attendance Summary (6 cards) ───────────────────────── */}
      <section>
        <SectionTitle>Attendance Summary</SectionTitle>
        {loading ? <SkeletonRow count={6} /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard title="Total Employees"    value={s.total_employees  ?? '—'} icon={Users}        color="blue"   />
            <StatCard title="Avg Attendance"     value={s.avg_attendance_pct  != null ? `${s.avg_attendance_pct}%`  : '—'} icon={TrendingUp}  color={attStatColor(s.avg_attendance_pct)}  />
            <StatCard title="Total Absent Days"  value={s.total_absent_days   ?? '—'} icon={TrendingDown} color="red"    />
            <StatCard title="Total Leave Days"   value={s.total_leave_days    ?? '—'} icon={Plane}        color="purple" />
            <StatCard title="Total Late Arrivals" value={s.total_late_arrivals ?? '—'} icon={Timer}       color="yellow" />
            <StatCard title="Working Days"       value={data?.working_days    ?? '—'} icon={CalendarDays} color="teal"   />
          </div>
        )}
      </section>

      {/* ── Section 2: Exception KPIs (4 cards) ───────────────────────────── */}
      <section>
        <SectionTitle>Exceptions</SectionTitle>
        {loading ? <SkeletonRow count={4} /> : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Max Absent"
              value={ex.max_absent?.days != null ? `${ex.max_absent.days}d` : '—'}
              subtitle={ex.max_absent?.name || ''}
              icon={TrendingDown} color="red"
            />
            <StatCard
              title="Max Leave"
              value={ex.max_leave?.days != null ? `${ex.max_leave.days}d` : '—'}
              subtitle={ex.max_leave?.name || ''}
              icon={Plane} color="purple"
            />
            <StatCard
              title="Most Late"
              value={ex.max_late?.count != null ? `${ex.max_late.count}×` : '—'}
              subtitle={ex.max_late?.name || ''}
              icon={Clock} color="yellow"
            />
            <StatCard
              title="Lowest Attendance"
              value={ex.lowest_attendance?.pct != null ? `${ex.lowest_attendance.pct}%` : '—'}
              subtitle={ex.lowest_attendance?.name || ''}
              icon={AlertTriangle} color="red"
            />
          </div>
        )}
      </section>

      {/* ── Section 3: Highlights (2 cards) ───────────────────────────────── */}
      <section>
        <SectionTitle>Highlights</SectionTitle>
        {loading ? <SkeletonRow count={2} /> : (
          <div className="grid grid-cols-2 gap-4">
            <StatCard
              title="Perfect Attendance"
              subtitle="employees"
              value={pos.perfect_count ?? '—'}
              icon={Star} color="green"
            />
            <StatCard
              title="Highest Attendance"
              subtitle={pos.highest_attendance?.name || ''}
              value={pos.highest_attendance?.pct != null ? `${pos.highest_attendance.pct}%` : '—'}
              icon={Trophy} color="green"
            />
          </div>
        )}
      </section>

      {/* ── Section 4: Compliance KPIs (5 cards) ──────────────────────────── */}
      <section>
        <SectionTitle>Compliance</SectionTitle>
        {loading ? <SkeletonRow count={5} /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard
              title="Attendance %"
              value={comp.attendance_pct != null ? `${comp.attendance_pct}%` : '—'}
              icon={TrendingUp} color={attStatColor(comp.attendance_pct)}
            />
            <StatCard
              title="Punctuality %"
              value={comp.punctuality_pct != null ? `${comp.punctuality_pct}%` : '—'}
              icon={Clock} color={attStatColor(comp.punctuality_pct)}
            />
            <StatCard
              title="Leave Utilization"
              value={comp.leave_utilization_pct != null ? `${comp.leave_utilization_pct}%` : '—'}
              icon={Plane} color="blue"
            />
            <StatCard
              title="Risk Employees"
              subtitle={`below ${riskThreshold}%`}
              value={comp.risk_count ?? '—'}
              icon={AlertTriangle} color="red"
            />
            <StatCard
              title="Missing Punch"
              value={comp.missing_punch_count ?? '—'}
              icon={Fingerprint} color="teal"
            />
          </div>
        )}
      </section>

      {/* ── Section 5: Department KPIs (5 cards) ──────────────────────────── */}
      <section>
        <SectionTitle>Department-wise Attendance</SectionTitle>
        {loading ? <SkeletonRow count={5} /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { key: 'teaching',    label: 'Teaching'    },
              { key: 'coordinator', label: 'Coordinator' },
              { key: 'admin',       label: 'Admin'       },
              { key: 'support',     label: 'Support'     },
              { key: 'leadership',  label: 'Leadership'  },
            ].map(({ key, label }) => (
              <StatCard
                key={key}
                title={label}
                subtitle={`${dept[key]?.count ?? 0} employees`}
                value={dept[key]?.pct != null ? `${dept[key].pct}%` : '—'}
                icon={Users}
                color={attStatColor(dept[key]?.pct)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Section 6: Alerts ─────────────────────────────────────────────── */}
      {!loading && alerts.length > 0 && (
        <section>
          <SectionTitle>Alerts</SectionTitle>
          <div className="glass-card p-4 space-y-2">
            {visibleAlerts.map((alert, idx) => {
              const { Icon, color } = ALERT_ICONS[alert.type] || { Icon: AlertTriangle, color: 'text-gray-400' };
              const msg = (ALERT_MSG[alert.type] || (a => `${a.employee} — ${a.value} ${a.unit}`))(alert);
              return (
                <div key={idx} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                  <Icon size={16} className={`${color} mt-0.5 shrink-0`} />
                  <span className="text-sm text-gray-700">{msg}</span>
                </div>
              );
            })}
            {alerts.length > 10 && (
              <button
                onClick={() => setAlertsExpanded(p => !p)}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 mt-1 font-medium"
              >
                {alertsExpanded
                  ? <><ChevronUp size={14} /> Show less</>
                  : <><ChevronDown size={14} /> Show {alerts.length - 10} more</>
                }
              </button>
            )}
          </div>
        </section>
      )}

      {/* ── Section 7: Risk Employees ─────────────────────────────────────── */}
      {!loading && riskList.length > 0 && (
        <section>
          <button
            onClick={() => setRiskExpanded(p => !p)}
            className="flex items-center gap-2 text-xs font-semibold text-red-600 uppercase tracking-wider mb-3 hover:text-red-800 transition-colors"
          >
            {riskExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Attendance Risk Employees (Below {riskThreshold}%)
            <span className="ml-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">
              {riskList.length}
            </span>
          </button>
          {riskExpanded && (
            <div className="glass-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Name', 'Department', 'Attendance', 'Absent Days'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {riskList.map((emp, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{emp.name}</td>
                      <td className="px-4 py-2.5 text-gray-500">{emp.dept}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                          emp.pct >= 95 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
                          emp.pct >= 90 ? 'text-amber-700 bg-amber-50 border-amber-200'   :
                          emp.pct >= 85 ? 'text-orange-700 bg-orange-50 border-orange-200' :
                                          'text-red-700 bg-red-50 border-red-200'
                        }`}>{emp.pct}%</span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{emp.absent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

    </div>
  );
}
